
import { useEffect, useRef, useState } from "react";
import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { JobCard } from "@/components/features/job-card";
import { LoadingState, EmptyState, PageHeader } from "@/components/features/states";
import { Plus, Package, Search, Megaphone, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Advertisement } from "@/lib/types";
import { FEATURE_FLAGS } from "@/lib/feature-flags";

const VERTICALS = [
  { key: "all", label: "all" },
  { key: "parcel", label: "parcel" },
  { key: "food", label: "food" },
  { key: "grocery", label: "grocery" },
  { key: "pharmacy", label: "pharmacy" },
  { key: "courier", label: "courier" },
  { key: "errand", label: "errand" },
  { key: "water", label: "water" },
] as const;

const FILTERS = [
  { key: "all", label: "all" },
  { key: "open", label: "open" },
  { key: "assigned", label: "assigned" },
  { key: "picked_up", label: "picked_up" },
  { key: "in_transit", label: "in_transit" },
  { key: "delivered", label: "delivered" },
  { key: "completed", label: "completed" },
  { key: "cancelled", label: "cancelled" },
];

function SponsoredAd({ ad }: { ad: Advertisement }) {
  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    api.ads.track({ id: ad.id, event: "impression" }).catch(() => {});
  }, [ad.id]);

  const onClick = () => {
    api.ads.track({ id: ad.id, event: "click" }).catch(() => {});
    if (ad.targetUrl) {
      window.open(ad.targetUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="flex gap-3 rounded-xl border border-amber-300/70 bg-amber-50/60 p-3 dark:border-amber-700/60 dark:bg-amber-950/20">
      {ad.imageUrl && (
        <img
          src={ad.imageUrl}
          alt=""
          className="h-16 w-16 shrink-0 rounded-lg object-cover"
          loading="lazy"
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-1.5">
          <Badge className="bg-amber-200 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/60 dark:text-amber-200">
            <Megaphone className="mr-1 h-3 w-3" /> Sponsored
          </Badge>
        </div>
        <p className="mt-1 line-clamp-1 text-sm font-semibold">{ad.title}</p>
        {ad.description && (
          <p className="line-clamp-2 text-xs text-muted-foreground">{ad.description}</p>
        )}
        {ad.ctaLabel && (
          <Button
            size="sm"
            variant="outline"
            className="mt-1.5 h-7 w-fit border-amber-400 text-amber-700 hover:bg-amber-100 dark:text-amber-200"
            onClick={onClick}
          >
            {ad.ctaLabel} <ExternalLink className="ml-1 h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

export function JobsListView() {
  const { user } = useAuthStore();
  const navigate = useUiStore((s) => s.navigate);
  const { t } = useTranslation();
  const isProvider = user?.activeRole === "provider";
  const [filter, setFilter] = useState("all");
  const [vertical, setVertical] = useState("all");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["jobs", "list", user?.activeRole, filter, vertical],
    queryFn: () =>
      api.jobs.list({
        role: user?.activeRole,
        status: filter === "all" ? undefined : filter,
        vertical: vertical === "all" ? undefined : vertical,
      }),
    enabled: !!user,
  });
  const jobs = data?.data?.jobs ?? [];

  // Sponsored ads — customers only. Gated by ADS_ENABLED feature flag.
  const showAds = FEATURE_FLAGS.ADS_ENABLED && !isProvider;
  const { data: adsData } = useQuery({
    queryKey: ["ads", "feed", vertical],
    queryFn: () => api.ads.feed({ vertical: vertical === "all" ? undefined : vertical }),
    enabled: showAds && !!user,
    staleTime: 60 * 1000,
  });
  const ads = adsData?.data?.ads ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title={isProvider ? t("jobs.findDeliveries") : t("jobs.myJobs")}
        subtitle={
          isProvider
            ? t("jobs.browseOpenJobs")
            : t("jobs.trackOrders")
        }
        action={
          !isProvider && (
            <Button size="sm" onClick={() => navigate("app/jobs/new")}>
              <Plus className="mr-1 h-4 w-4" /> {t("jobs.newOrder")}
            </Button>
          )
        }
      />

      {/* Vertical filter tabs */}
      <Tabs value={vertical} onValueChange={setVertical}>
        <TabsList className="w-full justify-start overflow-x-auto">
          {VERTICALS.map((v) => (
            <TabsTrigger key={v.key} value={v.key} className="flex-1 whitespace-nowrap">
              {v.key === "all" ? t("jobs.filterAll") : t(`verticals.${v.label}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Sponsored listings (customers only) */}
      {showAds && ads.length > 0 && (
        <div className="space-y-2">
          {ads.slice(0, 3).map((ad) => (
            <SponsoredAd key={ad.id} ad={ad} />
          ))}
        </div>
      )}

      {/* Status filter chips */}
      <div className="scroll-area -mx-3 flex gap-2 overflow-x-auto px-3 pb-1">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition ${
              filter === f.key
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/70"
            }`}
          >
            {f.key === "all" ? t("common.all") : t(`status.${f.key}`)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <LoadingState />
      ) : jobs.length === 0 ? (
        <EmptyState
          icon={isProvider ? <Search className="h-8 w-8" /> : <Package className="h-8 w-8" />}
          title={isProvider ? t("jobs.noJobsFound") : t("jobs.noJobs")}
          description={
            isProvider
              ? filter === "all"
                ? t("jobs.noOpenJobsDesc")
                : t("jobs.noMatchFilter")
              : t("jobs.placeFirstOrder")
          }
          action={
            !isProvider && (
              <Button size="sm" onClick={() => navigate("app/jobs/new")}>
                <Plus className="mr-1 h-4 w-4" /> Post a job
              </Button>
            )
          }
        />
      ) : (
        <div className="space-y-3">
          {jobs.map((job) => (
            <JobCard key={job.id} job={job} onClick={() => navigate(`app/jobs/${job.id}`)} />
          ))}
        </div>
      )}
    </div>
  );
}
